import { Button, Space } from 'antd'
import { observer } from 'mobx-react-lite'
import { FaFlagCheckered } from 'react-icons/fa'
import { GrPowerCycle } from 'react-icons/gr'
import { TbArrowBackUp } from 'react-icons/tb'

import { LossReasonModal } from './LossReasonModal'
import { LOSS_COMMENT_MAX_LENGTH, transportRequestActionsStoreFor } from './TransportRequestActionsStore'
import { TRANSPORT_REQUEST_TEXTS as T } from './TransportRequest.types'

import type { TransportRequestActionsStore } from './TransportRequestActionsStore'
import type {
    TransportRequestActionsConfig,
    TransportRequestCrudContextLike,
    TransportRequestTransition,
} from './TransportRequest.types'
import type { ReactNode } from 'react'

/** Параметри обробника `detailConfig.modeConfig.view.onButtons` ядра. */
export interface TransportRequestButtonsParams {
    buttons: Record<string, unknown>
    crudContext?: unknown
    content?: unknown[]
}

interface ButtonDeps {
    formContext?: { isEditable?: boolean | undefined } | undefined
}

/** Підписи, іконки й тон — як у легасі (`SPEC-022` §8.4). */
const BUTTONS: Record<TransportRequestTransition, { label: string; icon: ReactNode; primary: boolean }> = {
    convert: { label: T.convert, icon: <GrPowerCycle />, primary: true },
    lose: { label: T.lose, icon: <FaFlagCheckered />, primary: true },
    return_to_work: { label: T.returnToWork, icon: <TbArrowBackUp />, primary: false },
}

/**
 * Що кнопки беруть із картки — у звичайному (не MobX) класі: ядрова
 * `ButtonGroup` кладе `children` у глибоко спостережуваний стор і віддає
 * назад через `toJS`, і спостережуваний стор у пропсах повернувся б копією без
 * методів (той самий прийом, що в кнопках верифікації CRM).
 */
class ActionsSource {
    constructor(
        readonly store: TransportRequestActionsStore,
        readonly deps: ButtonDeps
    ) {}
}

const ActionButtons = observer(({ source }: { source: ActionsSource }) => {
    const { store, deps } = source
    const transitions = store.allowed(deps.formContext?.isEditable)

    if (transitions.length === 0) return null

    return (
        <Space size={8}>
            {transitions.map((transition) => {
                const { label, icon, primary } = BUTTONS[transition]

                return (
                    <Button
                        key={transition}
                        className="button-group-item"
                        type={primary ? 'primary' : 'default'}
                        icon={icon}
                        loading={store.pending === transition}
                        disabled={store.pending !== null && store.pending !== transition}
                        onClick={() => store.start(transition)}
                    >
                        {label}
                    </Button>
                )
            })}
        </Space>
    )
})

const LoseDialog = observer(({ store }: { store: TransportRequestActionsStore }) => (
    <LossReasonModal
        open={store.loseOpen}
        options={store.reasons}
        loading={store.reasonsLoading}
        value={store.lossReason}
        onChange={(value) => store.setLossReason(value)}
        withComment={store.config.withComment === true}
        comment={store.lossComment}
        onCommentChange={(value) => store.setLossComment(value)}
        commentMaxLength={LOSS_COMMENT_MAX_LENGTH}
        fieldError={store.lossReasonError}
        error={store.loseError}
        pending={store.pending === 'lose'}
        onSubmit={() => void store.submitLose()}
        onCancel={() => store.closeLose()}
    />
))

/**
 * Кнопки переходів у шапці картки запиту чи тендера (`SPEC-022` §8.4):
 * «Конвертація в заявку», «Програти», «Повернути в роботу».
 *
 * Видимість — **лише** `_permissions` запису (легасі питав сирі ролі й статус
 * на фронті, ДП-21): Rule уже звів право й дозволеність переходу. Лише в
 * перегляді — перехід не має йти з незбереженими правками форми.
 *
 * Підключення в ресурсі власника:
 * `detailConfig.modeConfig.view.onButtons = createTransportRequestButtons(config)`.
 */
export const createTransportRequestButtons =
    (config: TransportRequestActionsConfig) =>
    ({ buttons, crudContext, content }: TransportRequestButtonsParams): void => {
        if (crudContext === null || typeof crudContext !== 'object') return

        const context = crudContext as TransportRequestCrudContextLike
        const store = transportRequestActionsStoreFor(context, config)
        const depStore = { formContext: context.formContext }

        // Одна позиція групи — праворуч від «Редагувати» (sort 1000)
        buttons['transportRequestActions'] = {
            priority: 'button',
            sort: 2000,
            depStore,
            depFn: (deps: ButtonDeps) => store.allowed(deps.formContext?.isEditable).length > 0,
            children: <ActionButtons key="transport-request-actions" source={new ActionsSource(store, depStore)} />,
        }

        content?.push(<LoseDialog key="transport-request-lose" store={store} />)
    }
